import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "../api/client";
import { formatNumber, formatPrice, timeLeft } from "../lib/format";
import { AdminTabs } from "./AdminDashboard";

const STATES = ["", "ACTIVE", "SETTLED"];

export default function AdminSwaps() {
    const [swaps, setSwaps] = useState([]);
    const [state, setState] = useState("");
    const [coin, setCoin] = useState("");

    const load = async () => {
        try {
            const params = new URLSearchParams();
            if (state) params.set("state", state);
            if (coin) params.set("coin", coin);
            const r = await api.get(`/admin/swaps?${params.toString()}`);
            setSwaps(r.data);
        } catch {}
    };
    useEffect(() => { load(); const id = setInterval(load, 4000); return () => clearInterval(id); }, [state, coin]);

    const forceSettle = async (id) => {
        if (!window.confirm(`Force-settle swap ${id.slice(0, 8)}… at current oracle price?`)) return;
        try {
            await api.post(`/admin/swaps/${id}/force-settle`);
            toast.success("Swap force-settled");
            load();
        } catch (e) {
            toast.error("Force-settle failed", { description: formatApiError(e.response?.data?.detail) || e.message });
        }
    };

    return (
        <div className="bg-[#050505] min-h-[calc(100vh-3.5rem)] bg-grid-fine">
            <div className="max-w-[1480px] mx-auto px-4 lg:px-8 py-10 grid gap-6">
                <div>
                    <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-cyan-400">Admin · Swaps</div>
                    <h1 className="text-3xl font-medium tracking-tighter mt-2">All Swaps</h1>
                </div>
                <AdminTabs />

                <div className="flex gap-2">
                    <select data-testid="admin-swaps-state-filter" value={state} onChange={(e) => setState(e.target.value)} className="bg-[#0A0A0C] border border-[#1F1F24] outline-none px-3 py-2 rounded-sm font-mono text-xs">
                        {STATES.map((s) => <option key={s} value={s}>{s || "All states"}</option>)}
                    </select>
                    <input data-testid="admin-swaps-coin-filter" placeholder="filter by coin (e.g. BTC)" value={coin} onChange={(e) => setCoin(e.target.value.toUpperCase())} className="bg-[#0A0A0C] border border-[#1F1F24] outline-none px-3 py-2 rounded-sm font-mono text-xs flex-1" />
                </div>

                <div className="bg-[#0A0A0C] border border-[#1F1F24] rounded-sm overflow-x-auto">
                    <table className="w-full" data-testid="admin-swaps-table">
                        <thead>
                            <tr className="text-[9px] font-mono uppercase tracking-[0.22em] text-zinc-500">
                                {["State", "Coin", "Duration", "Notional", "Margin (B/B)", "Entry", "Current/Exit", "Bull PnL", "Bear PnL", "Outcome", "Time left", "Actions"].map((h) => (
                                    <th key={h} className="text-left px-3 py-3 font-normal">{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {swaps.length === 0 && (
                                <tr><td colSpan={12} className="text-center py-6 font-mono text-[11px] text-zinc-600">no swaps</td></tr>
                            )}
                            {swaps.map((s) => (
                                <tr key={s.id} className="border-t border-[#1F1F24] font-mono text-xs">
                                    <td className="px-3 py-2">
                                        <span className={`px-1.5 py-0.5 rounded-sm text-[10px] ${s.state === "ACTIVE" ? "bg-emerald-500/20 text-emerald-400" : "bg-zinc-500/20 text-zinc-400"}`}>
                                            {s.state}
                                        </span>
                                    </td>
                                    <td className="px-3 py-2 text-cyan-400">{s.coin}</td>
                                    <td className="px-3 py-2 text-zinc-400">{s.duration_key}</td>
                                    <td className="px-3 py-2 text-zinc-200">{formatNumber(s.notional)}</td>
                                    <td className="px-3 py-2 text-zinc-400">{formatNumber(s.margin_bull)} / {formatNumber(s.margin_bear)}</td>
                                    <td className="px-3 py-2 text-zinc-200">{formatPrice(s.entry_price)}</td>
                                    <td className="px-3 py-2 text-zinc-200">{formatPrice(s.exit_price || s.current_price)}</td>
                                    <td className={`px-3 py-2 ${(s.bull_pnl ?? s.pnl ?? 0) >= 0 ? "text-emerald-400" : "text-rose-400"}`}>{formatNumber(s.bull_pnl ?? (s.outcome === "BULLISH" ? s.pnl : -s.pnl))}</td>
                                    <td className={`px-3 py-2 ${(s.bear_pnl ?? 0) >= 0 ? "text-emerald-400" : "text-rose-400"}`}>{formatNumber(s.bear_pnl ?? (s.outcome === "BEARISH" ? s.pnl : -s.pnl))}</td>
                                    <td className="px-3 py-2 text-zinc-400">{s.outcome || "—"}{s.exit_type === "EARLY" ? " · early" : ""}</td>
                                    <td className="px-3 py-2 text-zinc-500">{s.state === "ACTIVE" ? timeLeft(s.expiry_unix) : "—"}</td>
                                    <td className="px-3 py-2 text-right">
                                        {s.state === "ACTIVE" && (
                                            <button data-testid={`admin-force-settle-${s.id}`} onClick={() => forceSettle(s.id)} className="text-[10px] uppercase tracking-widest text-amber-400 hover:text-amber-300">force settle</button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}
